import { Box, IconButton, styled, useTheme } from '@mui/material';
import { createColumnHelper, Row } from '@tanstack/react-table';
import { useMemo } from 'react';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/chevron-right.svg';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/chevron-down.svg';
import { DealWithTimeLogs } from '../../../types';
import { Typography } from '../../../components/Typography/Typography';

const ChipWrapper = styled('div')`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surfaceBackground.bg4};
  padding: 4px 8px;
`;

const CellWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const columnHelper = createColumnHelper<DealWithTimeLogs>();

export const useOpChargeSummaryTableColumns = () => {
  const { colors } = useTheme();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        cell: (info) => {
          return (
            <Box
              style={{
                height: '48px',
                borderRadius: '4px 0 0 4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                overflow: 'hidden',
              }}
            >
              <IconButton
                size='small'
                style={{ padding: 0, visibility: info.row.getCanExpand() ? 'visible' : 'hidden' }}
                onClick={info.row.getCanExpand() ? info.row.getToggleExpandedHandler() : undefined}
              >
                {info.row.getIsExpanded() ? <ArrowDownIcon /> : <ArrowRightIcon />}
              </IconButton>
              <AvatarCell
                avatarBoxStyle={{
                  background: colors.surfaceIndicator.needsReview,
                }}
                avatarLetterStyle={{
                  color: colors.text.main,
                }}
                name={info.getValue()}
              />
            </Box>
          );
        },
        header: () => <TableHeaderCell text={'Deal'} />,
        meta: {
          width: '400px',
          maxWidth: '400px',
          minWidth: '400px',
          tdStyles: { paddingLeft: '16px', borderRight: `none` },
          thStyles: { borderRight: `none`, paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor(`totalTime`, {
        cell: (info) => {
          const logCount = info.row.original.dealTimeLogs.length;
          return (
            <CellWrapper>
              {Boolean(logCount) && (
                <ChipWrapper>
                  <Typography variant='srOnly' color={colors.textStatus.active}>
                    {logCount} {logCount > 1 ? 'Submitters' : 'Submitter'}
                  </Typography>
                </ChipWrapper>
              )}
            </CellWrapper>
          );
        },
        header: () => <TableHeaderCell text={'Time Charged (%)'} />,
        sortingFn: (a: Row<DealWithTimeLogs>, b: Row<DealWithTimeLogs>) => {
          // sort by deal time logs length
          const aLogsLength = a.original.dealTimeLogs.length;
          const bLogsLength = b.original.dealTimeLogs.length;
          if (aLogsLength > bLogsLength) return -1;
          if (aLogsLength < bLogsLength) return 1;
          return 0;
        },
        meta: {
          tdStyles: { paddingLeft: '16px', borderRight: `none`, borderLeft: 'none' },
          thStyles: { borderRight: `none`, paddingLeft: '16px', borderLeft: 'none' },
        },
      }),
    ];
  }, [colors.surfaceIndicator.needsReview, colors.text.main, colors.textStatus.active]);

  return columns;
};
